import {
  Invitation, Inviter, Registerer, RegistererState, SessionState, UserAgent, Web,
} from "sip.js";
import {
  createIcons, Check, ChevronDown, Clock3, Delete, Download, Grid3X3,
  History, Info, ListChecks, LogIn, LogOut, MicOff, Pause, Phone, PhoneForwarded,
  PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, RefreshCw, Search,
  Settings2, ShieldCheck, Users, X,
} from "lucide";

const ICONS = {Check,ChevronDown,Clock3,Delete,Download,Grid3x3:Grid3X3,History,Info,ListChecks,LogIn,LogOut,MicOff,Pause,Phone,PhoneForwarded,PhoneIncoming,PhoneMissed,PhoneOff,PhoneOutgoing,RefreshCw,Search,Settings2,ShieldCheck,Users,X};
const $ = (id) => document.getElementById(id);
const state = {
  accessToken: null, account: null, workspaces: [], membershipId: null,
  enrollmentId: null, bootstrap: null, pbxSession: null, userAgent: null,
  registerer: null, call: null, callDirection: null, callStarted: null,
  incomingContext: null,
  muted: false, held: false, queues: [], agents: [], callbacks: [], callbackFilter: "open", eventAbort: null,
  timers: [], installPrompt: null, registrationRefresh: null,
  phoneRecovery: null, serviceWorker: null, pushEnabled: false,
};
const deviceKey = localStorage.getItem("dialstop_phone_device") || `web:${crypto.randomUUID()}`;
localStorage.setItem("dialstop_phone_device", deviceKey);

function refreshIcons(root = document) { createIcons({icons: ICONS, attrs: {"stroke-width": 1.8}, root}); }
function show(view) {
  ["authView", "pendingView", "phoneView"].forEach((id) => $(id).classList.toggle("hidden", id !== view));
}
function toast(message, error = false) {
  const el = $("toast"); el.textContent = message; el.className = `toast show${error ? " error" : ""}`;
  clearTimeout(toast.timer); toast.timer = setTimeout(() => { el.className = "toast"; }, 3200);
}
function setBusy(button, busy) { button.disabled = busy; button.dataset.busy = busy ? "1" : "0"; }
function cleanCode(value) { return value.replace(/\D/g, "").slice(0, 8); }
function safeError(error) { return error?.message || "Something went wrong"; }
const hiddenDisplayNumbers = new Set(["447756011322"]);
function displayParty(value, fallback = "Private number") {
  const rendered = String(value ?? "");
  return hiddenDisplayNumbers.has(rendered.replace(/\D/g, "")) ? fallback : rendered;
}
function device() { return {device_key: deviceKey, name: navigator.userAgent.includes("Mobile") ? "Mobile browser" : "Web browser", platform: "web"}; }

async function ensureMicrophoneAccess() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("This browser cannot access a microphone. Open DialStop Phone in Safari or Chrome.");
  }
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({audio: true, video: false});
  } catch (error) {
    if (["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(error?.name)) {
      throw new Error("Microphone access is blocked. Allow it for phone.dialstop.net, then call again.");
    }
    if (["NotFoundError", "DevicesNotFoundError"].includes(error?.name)) {
      throw new Error("No microphone was found on this device.");
    }
    if (["NotReadableError", "TrackStartError"].includes(error?.name)) {
      throw new Error("The microphone is in use by another app. Close it and call again.");
    }
    throw error;
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
}

async function jsonRequest(url, options = {}) {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {"Content-Type": "application/json", ...(options.headers || {})},
  });
  if (response.status === 204) return null;
  let body = null; try { body = await response.json(); } catch (_) {}
  if (!response.ok) throw new Error(body?.detail || `Request failed (${response.status})`);
  return body;
}
async function central(path, options = {}, authenticated = true) {
  const headers = {...(options.headers || {})};
  if (authenticated && state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`;
  return jsonRequest(path, {...options, headers});
}
async function pbx(path, options = {}) {
  const url = `${state.bootstrap.pbx.api_url}${path}`;
  const headers = {...(options.headers || {})};
  if (state.pbxSession?.control_token) headers.Authorization = `Bearer ${state.pbxSession.control_token}`;
  const response = await fetch(url, {...options, mode: "cors", headers: {"Content-Type": "application/json", ...headers}});
  let body = null; try { body = await response.json(); } catch (_) {}
  if (!response.ok) throw new Error(body?.detail || `Phone service failed (${response.status})`);
  return body;
}

function acceptCentralSession(result) {
  state.accessToken = result.access_token; state.account = result.account;
  state.workspaces = result.workspaces || state.workspaces;
}
async function refreshCentralSession() {
  const result = await jsonRequest("/web/v1/auth/refresh", {method: "POST", body: JSON.stringify({device_key: deviceKey})});
  acceptCentralSession(result); return result;
}
async function restore() {
  try {
    await refreshCentralSession();
    const requested = new URLSearchParams(location.search).get("workspace");
    const active = state.workspaces.find((item) => item.status === "active" && item.id === requested)
      || state.workspaces.find((item) => item.status === "active");
    const pending = state.workspaces.find((item) => item.status === "pending");
    if (active) await openWorkspace(active.id);
    else if (pending) { state.membershipId = pending.id; showPending(pending); }
    else show("authView");
  } catch (_) { show("authView"); }
}

function showPending(item) {
  show("pendingView");
  $("pendingText").textContent = `${item.tenant_name || "Your team"} has received your request. This page checks automatically.`;
  clearIntervals(); state.timers.push(setInterval(checkPending, 3000));
}
async function checkPending() {
  try {
    if (state.enrollmentId) {
      const result = await central(`/v1/enrollments/${state.enrollmentId}`);
      if (result.status === "active" || result.status === "approved") return refreshCentralSession().then(() => openWorkspace(result.membership_id));
      if (result.status === "rejected") throw new Error("Your access request was declined");
    }
    await refreshCentralSession();
    const active = state.workspaces.find((item) => item.status === "active");
    if (active) await openWorkspace(active.id);
  } catch (error) { toast(safeError(error), true); }
}

async function openWorkspace(membershipId) {
  clearIntervals(); await stopPhone();
  state.membershipId = membershipId;
  state.bootstrap = await central(`/v1/app/bootstrap?membership_id=${encodeURIComponent(membershipId)}`);
  state.pbxSession = await jsonRequest(`${state.bootstrap.pbx.api_url}/v1/mobile/session`, {
    method: "POST", mode: "cors", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({provisioning_ticket: state.bootstrap.provisioning_ticket}),
  });
  renderWorkspace(); show("phoneView");
  await startPhoneWithRetry();
  await Promise.allSettled([loadQueues(), loadAgents(), loadHistory(), loadCallbacks()]);
  await refreshPushState().catch(() => setNotificationStatus("Alerts need attention", false));
  startEvents();
  state.timers.push(setInterval(renewLeases, 4 * 60 * 1000));
  state.timers.push(setInterval(ensurePhoneHealthy, 15 * 1000));
  state.timers.push(setInterval(() => central("/v1/devices/heartbeat", {method: "POST"}).catch(() => {}), 60 * 1000));
}

function pushAvailable() {
  return Boolean(
    state.pbxSession?.push?.supported
    && state.pbxSession.push.public_key
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
}
function setNotificationStatus(message, enabled = state.pushEnabled) {
  state.pushEnabled = enabled;
  $("notificationStatus").textContent = message;
  $("notificationButton").textContent = enabled ? "Disable alerts" : "Enable alerts";
  $("notificationButton").disabled = !pushAvailable();
}
function applicationServerKey(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const binary = atob((value + padding).replaceAll("-", "+").replaceAll("_", "/"));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
async function savePushSubscription(subscription) {
  const value = subscription.toJSON();
  if (!value.endpoint || !value.keys?.p256dh || !value.keys?.auth) throw new Error("This browser returned an incomplete notification subscription");
  await pbx("/v1/mobile/push/subscription", {
    method: "PUT",
    body: JSON.stringify({endpoint: value.endpoint, p256dh: value.keys.p256dh, auth: value.keys.auth}),
  });
}
async function refreshPushState() {
  if (!pushAvailable()) {
    setNotificationStatus("Install this phone on a supported iPhone to use closed-app alerts", false);
    return;
  }
  const registration = state.serviceWorker || await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (Notification.permission === "granted" && subscription) {
    await savePushSubscription(subscription);
    setNotificationStatus("Ready while this phone is closed", true);
    return;
  }
  if (Notification.permission === "denied") {
    setNotificationStatus("Notifications are blocked in iPhone Settings", false);
    return;
  }
  setNotificationStatus("Enable once to receive calls while this phone is closed", false);
}
async function togglePushNotifications() {
  const button = $("notificationButton");
  setBusy(button, true);
  try {
    const registration = state.serviceWorker || await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (state.pushEnabled) {
      await pbx("/v1/mobile/push/subscription", {method: "DELETE"});
      await existing?.unsubscribe();
      setNotificationStatus("Closed-app alerts are disabled", false);
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("Allow notifications in iPhone Settings to receive closed-app calls");
    const subscription = existing || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(state.pbxSession.push.public_key),
    });
    await savePushSubscription(subscription);
    setNotificationStatus("Ready while this phone is closed", true);
    toast("Closed-app call alerts enabled");
  } catch (error) {
    toast(safeError(error), true);
    await refreshPushState().catch(() => {});
  } finally { setBusy(button, false); }
}
async function handlePushOpen(message) {
  const workspaceId = message?.workspace_id;
  if (workspaceId && workspaceId !== state.membershipId) await openWorkspace(workspaceId);
  else if (state.membershipId) await renewLeases();
  switchPane("dialer");
  toast("Connecting the incoming call");
}

function renderWorkspace() {
  const tenant = state.bootstrap.tenant; const agent = state.pbxSession.agent;
  $("workspaceName").textContent = tenant.name;
  $("agentIdentity").textContent = `${agent.display_name} · ${agent.extension}`;
  $("settingsWorkspace").textContent = tenant.name; $("settingsExtension").textContent = agent.extension;
  $("callbackNavButton").classList.toggle("hidden", !state.pbxSession.callback_enabled);
  const select = $("workspaceSelect"); select.replaceChildren();
  state.workspaces.filter((item) => item.status === "active").forEach((item) => {
    const option = document.createElement("option"); option.value = item.id; option.textContent = item.tenant_name;
    option.selected = item.id === state.membershipId; select.append(option);
  });
}

function registrationStatus(kind, label) {
  const badge = $("registrationBadge"); badge.className = `status-badge ${kind}`; badge.textContent = label;
  $("phoneStatus").textContent = kind === "ready" ? "Ready for inbound and outbound calls" : label;
  $("settingsConnection").textContent = label;
}
async function startPhoneWithRetry() {
  registrationStatus("pending", "Connecting");
  for (let attempt = 0; attempt < 10; attempt += 1) {
    try { await startPhone(); return; } catch (error) {
      if (attempt === 9) { registrationStatus("offline", "Phone offline"); throw error; }
      await new Promise((resolve) => setTimeout(resolve, 1000 + attempt * 600));
    }
  }
}
async function startPhone() {
  const sip = state.pbxSession.sip;
  const uri = UserAgent.makeURI(`sip:${sip.username}@${sip.server}`);
  if (!uri || !sip.websocket_url) throw new Error("Web phone configuration is incomplete");
  const ua = new UserAgent({
    uri, displayName: state.pbxSession.agent.display_name,
    authorizationUsername: sip.auth_username, authorizationPassword: sip.password,
    transportOptions: {
      server: sip.websocket_url,
      connectionTimeout: 8,
      keepAliveInterval: 30,
      keepAliveDebounce: 10,
    },
    sessionDescriptionHandlerFactoryOptions: {
      peerConnectionConfiguration: {
        iceServers: Array.isArray(sip.ice_servers) ? sip.ice_servers : [],
      },
    },
    delegate: {onInvite: receiveInvite}, logLevel: "warn",
  });
  // Mobile browsers throttle background timers. Keep a wide renewal buffer so
  // a delayed refresh cannot silently expire an otherwise healthy phone.
  const registerer = new Registerer(ua, {expires: 1800, refreshFrequency: 70});
  state.userAgent = ua; state.registerer = registerer;
  let registrationSettled = false;
  let registrationTimer;
  const registered = new Promise((resolve, reject) => {
    registrationTimer = setTimeout(() => reject(new Error("Phone registration timed out")), 8000);
    registerer.stateChange.addListener((value) => {
      if (registrationSettled) return;
      if (value === RegistererState.Registered) {
        registrationSettled = true; clearTimeout(registrationTimer); resolve();
      } else if (value === RegistererState.Unregistered || value === RegistererState.Terminated) {
        registrationSettled = true; clearTimeout(registrationTimer); reject(new Error("Phone registration was rejected"));
      }
    });
  });
  registerer.stateChange.addListener((value) => {
    if (value === RegistererState.Registered) registrationStatus("ready", "Online");
    if (value === RegistererState.Unregistered || value === RegistererState.Terminated) registrationStatus("offline", "Phone offline");
  });
  try {
    await ua.start(); await registerer.register(); await registered;
  } catch (error) {
    registrationSettled = true; clearTimeout(registrationTimer);
    try { await registerer.unregister(); } catch (_) {}
    try { await ua.stop(); } catch (_) {}
    if (state.userAgent === ua) { state.userAgent = null; state.registerer = null; }
    throw error;
  }
}
async function stopPhone() {
  state.eventAbort?.abort(); state.eventAbort = null;
  try { if (state.call) await endCall(); } catch (_) {}
  try { await state.registerer?.unregister(); } catch (_) {}
  try { await state.userAgent?.stop(); } catch (_) {}
  state.registerer = null; state.userAgent = null; state.registrationRefresh = null;
}
async function refreshRegistration() {
  if (!state.registerer) return;
  if (state.registrationRefresh) return state.registrationRefresh;
  const registerer = state.registerer;
  state.registrationRefresh = registerer.register()
    .catch((error) => {
      if (error?.constructor?.name !== "RequestPendingError") throw error;
    })
    .finally(() => {
      if (state.registerer === registerer) state.registrationRefresh = null;
    });
  return state.registrationRefresh;
}
async function renewLeases() {
  try { await refreshRegistration(); await pbx("/v1/mobile/session/renew", {method: "POST"}); await central("/v1/devices/heartbeat", {method: "POST"}); }
  catch (error) { registrationStatus("offline", "Reconnecting"); try { await refreshCentralSession(); await openWorkspace(state.membershipId); } catch (_) {} }
}

async function ensurePhoneHealthy() {
  if (!state.membershipId || !state.pbxSession || state.call || !navigator.onLine) return;
  if (state.registerer?.state === RegistererState.Registered) return;
  if (state.phoneRecovery) return state.phoneRecovery;
  const membershipId = state.membershipId;
  state.phoneRecovery = (async () => {
    registrationStatus("pending", "Reconnecting");
    try {
      if (state.registerer && state.registerer.state !== RegistererState.Terminated) {
        await refreshRegistration();
        await new Promise((resolve) => setTimeout(resolve, 2500));
        if (state.registerer?.state === RegistererState.Registered) return;
      }
      if (state.membershipId === membershipId) await openWorkspace(membershipId);
    } catch (_) {
      registrationStatus("offline", "Phone offline");
    }
  })().finally(() => { state.phoneRecovery = null; });
  return state.phoneRecovery;
}

async function markCallExit(intent) {
  if (!state.pbxSession?.control_token) return;
  await pbx("/v1/mobile/calls/exit-intent", {
    method: "POST",
    body: JSON.stringify({intent}),
  });
}

async function loadIncomingContext(invitation) {
  try {
    const context = await pbx("/v1/mobile/incoming-context");
    if (state.call !== invitation || context.kind !== "transfer") return;
    state.incomingContext = context;
    const source = context.transferred_by?.display_name || "another agent";
    if (invitation.state === SessionState.Established) {
      showActiveCall(context.original_caller || remoteParty(invitation), "Transferred call");
    } else {
      $("incomingCaller").textContent = `Transfer from ${source}`;
    }
  } catch (_) {}
}
function receiveInvite(invitation) {
  if (state.call) { invitation.reject({statusCode: 486}); return; }
  state.call = invitation; state.callDirection = "Inbound"; state.incomingContext = null; watchCall(invitation);
  $("incomingCaller").textContent = remoteParty(invitation); $("incomingDialog").showModal();
  loadIncomingContext(invitation);
  if (navigator.vibrate) navigator.vibrate([250, 150, 250]);
}
function remoteParty(session) { return displayParty(session.remoteIdentity?.displayName || session.remoteIdentity?.uri?.user || "Unknown caller"); }
function watchCall(session) {
  session.stateChange.addListener((value) => {
    if (value === SessionState.Established) {
      state.callStarted = Date.now(); attachRemoteAudio(session);
      showActiveCall(
        state.incomingContext?.original_caller || null,
        state.incomingContext?.kind === "transfer" ? "Transferred call" : null,
      );
      $("incomingDialog").open && $("incomingDialog").close();
    }
    if (value === SessionState.Terminated) finishCall();
  });
}
function attachRemoteAudio(session) {
  const pc = session.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;
  const tracks = pc.getReceivers().map((receiver) => receiver.track).filter((track) => track?.kind === "audio");
  const audio = $("remoteAudio"); audio.srcObject = new MediaStream(tracks); audio.play().catch(() => toast("Tap Answer or Call to enable audio", true));
}
async function answerCall() {
  if (!(state.call instanceof Invitation)) return;
  try { await state.call.accept({sessionDescriptionHandlerOptions: {constraints: {audio: true, video: false}}}); }
  catch (error) { toast(safeError(error), true); finishCall(); }
}
async function placeCall() {
  const targetValue = $("dialNumber").value.trim().replace(/[^0-9*#+]/g, "");
  if (!targetValue || !state.userAgent || state.call) return;
  const target = UserAgent.makeURI(`sip:${targetValue}@${state.pbxSession.sip.server}`);
  if (!target) return toast("Enter a valid number", true);
  const callButton = $("callButton");
  try {
    setBusy(callButton, true);
    toast("Allow microphone access to place the call");
    await ensureMicrophoneAccess();
    const inviter = new Inviter(state.userAgent, target, {sessionDescriptionHandlerOptions: {constraints: {audio: true, video: false}}});
    state.call = inviter; state.callDirection = "Outbound"; watchCall(inviter); showActiveCall(targetValue, "Calling");
    await inviter.invite();
  } catch (error) { toast(safeError(error), true); finishCall(); }
  finally { setBusy(callButton, false); }
}
async function endCall() {
  const call = state.call; if (!call) return;
  if (call.state === SessionState.Established) {
    await markCallExit("end").catch(() => {});
    await call.bye();
  }
  else if (call instanceof Invitation) await call.reject();
  else if (call instanceof Inviter) await call.cancel();
}
function finishCall() {
  state.call = null; state.callStarted = null; state.incomingContext = null; state.muted = false; state.held = false;
  $("remoteAudio").srcObject = null; $("incomingDialog").open && $("incomingDialog").close();
  $("activeCall").classList.add("hidden"); $("idlePhone").classList.remove("hidden");
  $("inCallDialpad").classList.add("hidden"); $("muteButton").classList.remove("active"); $("holdButton").classList.remove("active");
  setTimeout(loadHistory, 1500);
}
function showActiveCall(party = null, status = null) {
  $("idlePhone").classList.add("hidden"); $("activeCall").classList.remove("hidden");
  $("callParty").textContent = displayParty(party || remoteParty(state.call)); $("callDirection").textContent = status || state.callDirection || "Connected";
}
function updateCallTimer() {
  if (!state.callStarted) return $("callTimer").textContent = "00:00";
  const seconds = Math.floor((Date.now() - state.callStarted) / 1000);
  $("callTimer").textContent = `${String(Math.floor(seconds / 60)).padStart(2,"0")}:${String(seconds % 60).padStart(2,"0")}`;
}
function toggleMute() {
  const pc = state.call?.sessionDescriptionHandler?.peerConnection; if (!pc) return;
  state.muted = !state.muted; pc.getSenders().forEach((sender) => { if (sender.track?.kind === "audio") sender.track.enabled = !state.muted; });
  $("muteButton").classList.toggle("active", state.muted); $("muteButton").querySelector("span").textContent = state.muted ? "Unmute" : "Mute";
}
async function toggleHold() {
  if (!state.call || state.call.state !== SessionState.Established) return;
  try {
    state.held = !state.held;
    await state.call.invite({sessionDescriptionHandlerModifiers: state.held ? [Web.holdModifier] : []});
    $("holdButton").classList.toggle("active", state.held); $("holdButton").querySelector("span").textContent = state.held ? "Resume" : "Hold";
  } catch (error) { state.held = !state.held; toast("Hold could not be changed", true); }
}
function sendDtmf(tone) {
  if (!state.call || state.call.state !== SessionState.Established) return;
  const handler = state.call.sessionDescriptionHandler;
  if (typeof handler?.sendDtmf === "function") handler.sendDtmf(tone, {duration: 160, interToneGap: 70});
}

async function loadQueues() {
  const result = await pbx("/v1/mobile/queue-state"); state.queues = result.queues || [];
  renderQueues(); setPresence(result.availability || "available");
}
function setPresence(value) {
  $("presenceLabel").textContent = value === "away" ? "Away" : "Available";
  $("presenceButton").classList.toggle("away", value === "away");
}
async function togglePresence() {
  if (!state.queues.length) return toast("No queue is assigned", true);
  const next = $("presenceButton").classList.contains("away") ? "available" : "away";
  try {
    const result = await pbx("/v1/mobile/queue-state", {method: "PUT", body: JSON.stringify({queue_ref: state.queues[0].queue_ref, availability: next})});
    state.queues = result.queues; setPresence(result.availability); renderQueues();
  } catch (error) { toast(safeError(error), true); }
}
function renderQueues() {
  const list = $("queueList"); list.replaceChildren();
  if (!state.queues.length) return list.append(empty("No queues assigned"));
  state.queues.forEach((queue) => {
    const row = rowShell(queue.name, `Extension ${queue.queue_ref}`); const toggle = document.createElement("label"); toggle.className = "switch";
    const input = document.createElement("input"); input.type = "checkbox"; input.checked = queue.joined;
    input.addEventListener("change", async () => { input.disabled = true; try { const result = await pbx("/v1/mobile/queue-state", {method:"PUT", body:JSON.stringify({queue_ref:queue.queue_ref, joined:input.checked})}); state.queues=result.queues; renderQueues(); } catch(error){ input.checked=!input.checked; toast(safeError(error),true); } finally { input.disabled=false; } });
    toggle.append(input, document.createElement("span")); row.append(toggle); list.append(row);
  });
}
async function loadAgents() { const result = await pbx("/v1/mobile/transfer-targets"); state.agents = result.targets || []; renderAgents(); }
function renderAgents() {
  const list = $("agentList"); list.replaceChildren(); if (!state.agents.length) return list.append(empty("No other agents yet"));
  state.agents.forEach((agent) => { const row = rowShell(agent.display_name, `Extension ${agent.extension}`, initials(agent.display_name)); const status=document.createElement("span"); status.className=`row-status ${agent.status}`; status.textContent=agent.status.replaceAll("_"," "); row.append(status); list.append(row); });
}
function callOutcome(call) {
  const labels = {
    answered: "Answered", declined: "Declined", busy: "Busy",
    no_answer: "No answer", invalid_number: "Invalid number",
    unreachable: "Unreachable", network_failure: "Network failure",
    cancelled: "Cancelled", failed: "Failed",
  };
  const key = call.call_outcome || (call.answered_at ? "answered" : "failed");
  return {key, label: labels[key] || "Failed", answered: key === "answered"};
}
async function loadHistory() {
  if (!state.pbxSession) return; const result = await pbx("/v1/mobile/calls?limit=50"); const list=$("historyList"); list.replaceChildren();
  if (!result.calls?.length) return list.append(empty("No calls on this extension yet"));
  result.calls.forEach((call) => {
    const inbound=call.direction==="inbound"; const party=displayParty((inbound?call.from_uri:call.to_uri)||"Unknown"); const outcome=callOutcome(call);
    const detail=outcome.answered ? `${outcome.label} - ${duration(call.bill_seconds)}` : outcome.label;
    const row=rowShell(party.replace(/^sip:/,""), `${formatDate(call.started_at)} - ${detail}`);
    const icon=document.createElement("span"); icon.className=`history-icon ${outcome.answered?"answered":"missed"}`; icon.innerHTML=`<i data-lucide="${outcome.answered?(inbound?"phone-incoming":"phone-outgoing"):"phone-missed"}"></i>`; row.prepend(icon); list.append(row);
  }); refreshIcons(list);
}
async function loadCallbacks() {
  if (!state.pbxSession?.callback_enabled) return;
  const requestedState = state.callbackFilter === "all" ? "all" : "open";
  const result = await pbx(`/v1/mobile/callbacks?state=${requestedState}&limit=200`);
  state.callbacks = result.callbacks || [];
  renderCallbacks(result.open_count || 0);
}
function renderCallbacks(openCount = state.callbacks.filter((item) => !["completed", "closed"].includes(item.state)).length) {
  const list = $("callbackList"); list.replaceChildren();
  const badge = $("callbackBadge"); badge.textContent = openCount > 99 ? "99+" : String(openCount); badge.classList.toggle("hidden", !openCount);
  let callbacks = state.callbacks;
  if (state.callbackFilter === "mine") callbacks = callbacks.filter((item) => item.claimed_by_agent_id === state.pbxSession.agent.id);
  if (!callbacks.length) return list.append(empty("No callback requests in this view"));
  callbacks.forEach((item) => {
    const card = document.createElement("article"); card.className = "callback-card";
    const head = document.createElement("div"); head.className = "callback-card-head";
    const copy = document.createElement("div");
    const visibleNumber = displayParty(item.number_e164);
    const title = document.createElement("strong"); title.textContent = item.contact_name || visibleNumber;
    const detail = document.createElement("span"); detail.textContent = item.contact_name ? visibleNumber : (item.contact_email || "Press-2 request");
    copy.append(title, detail);
    const status = document.createElement("span"); status.className = `callback-status ${item.state}`; status.textContent = item.state.replaceAll("_", " ");
    head.append(copy, status);
    const meta = document.createElement("div"); meta.className = "callback-meta";
    [item.campaign_name || "Campaign", formatDate(item.pressed_at), `${item.attempt_count || 0} attempt${item.attempt_count === 1 ? "" : "s"}`].forEach((value) => { const node = document.createElement("span"); node.textContent = value; meta.append(node); });
    const actions = document.createElement("div"); actions.className = "callback-card-actions";
    const mine = item.claimed_by_agent_id === state.pbxSession.agent.id;
    if (["new", "retry"].includes(item.state) && !item.claimed_by_agent_id) {
      const call = document.createElement("button"); call.className = "primary command"; call.textContent = "Claim and call";
      call.addEventListener("click", () => claimCallback(item, call)); actions.append(call);
    } else if (mine && item.state === "calling") {
      const wait = document.createElement("span"); wait.className = "callback-wait"; wait.textContent = "Call in progress"; actions.append(wait);
    } else if (mine) {
      [["Complete", "completed", "primary"], ["Retry later", "retry_later", "secondary"], ["Release", "release", "quiet"]].forEach(([label, outcome, style]) => {
        const button = document.createElement("button"); button.className = `${style} command`; button.textContent = label;
        button.addEventListener("click", () => updateCallback(item, outcome, button)); actions.append(button);
      });
    } else if (item.claimed_by_agent_name) {
      const owner = document.createElement("span"); owner.className = "callback-wait"; owner.textContent = `Owned by ${item.claimed_by_agent_name}`; actions.append(owner);
    }
    card.append(head, meta, actions); list.append(card);
  });
}
async function claimCallback(item, button) {
  setBusy(button, true);
  try {
    await pbx(`/v1/mobile/callbacks/${item.id}/claim-and-call`, {method: "POST"});
    toast("Answer the incoming call, then we will connect the customer");
    await loadCallbacks();
  } catch (error) { toast(safeError(error), true); }
  finally { setBusy(button, false); }
}
async function updateCallback(item, outcome, button) {
  setBusy(button, true);
  try {
    if (outcome === "release") await pbx(`/v1/mobile/callbacks/${item.id}/release`, {method: "POST"});
    else await pbx(`/v1/mobile/callbacks/${item.id}/disposition`, {method: "POST", body: JSON.stringify({disposition: outcome})});
    await loadCallbacks();
  } catch (error) { toast(safeError(error), true); }
  finally { setBusy(button, false); }
}
function rowShell(title, subtitle, avatarText = null) { const row=document.createElement("div"); row.className="list-row"; const main=document.createElement("div"); main.className="list-main"; if(avatarText!==null){const avatar=document.createElement("span");avatar.className="avatar";avatar.textContent=avatarText;main.append(avatar);} const copy=document.createElement("div");copy.className="list-copy";const strong=document.createElement("strong");strong.textContent=title;const span=document.createElement("span");span.textContent=subtitle;copy.append(strong,span);main.append(copy);row.append(main);return row; }
function empty(text){const el=document.createElement("div");el.className="empty-state";el.textContent=text;return el;}
function initials(name){return name.split(/\s+/).slice(0,2).map((part)=>part[0]).join("").toUpperCase();}
function formatDate(value){return new Intl.DateTimeFormat(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}).format(new Date(value));}
function duration(seconds){const value=Number(seconds)||0;return value>=60?`${Math.floor(value/60)}m ${value%60}s`:`${value}s`;}

async function startEvents() {
  state.eventAbort?.abort(); const controller=new AbortController(); state.eventAbort=controller; let cursor=0;
  while (!controller.signal.aborted && state.pbxSession) {
    try {
      const response=await fetch(`${state.bootstrap.pbx.api_url}/v1/mobile/events?after=${cursor}`,{headers:{Authorization:`Bearer ${state.pbxSession.control_token}`},signal:controller.signal,mode:"cors"});
      if(!response.ok) throw new Error("Event stream disconnected");
      const reader=response.body.getReader(); const decoder=new TextDecoder(); let buffer="";
      while(!controller.signal.aborted){const {value,done}=await reader.read();if(done)break;buffer+=decoder.decode(value,{stream:true});let split;while((split=buffer.indexOf("\n\n"))>=0){const block=buffer.slice(0,split);buffer=buffer.slice(split+2);const id=block.match(/^id:\s*(.+)$/m)?.[1];const data=block.match(/^data:\s*(.+)$/m)?.[1];if(id)cursor=Number(id)||cursor;if(data){try{handleEvent(JSON.parse(data));}catch(_){}}}}
    } catch(error){if(controller.signal.aborted)return;await new Promise((resolve)=>setTimeout(resolve,1500));}
  }
}
function handleEvent(event){if(event.type==="agent.queue_state"&&event.queue_state){state.queues=event.queue_state.queues||[];setPresence(event.queue_state.availability);renderQueues();} if(event.type?.startsWith("callback.")){loadCallbacks().catch(()=>{});} if(event.type?.startsWith("call.")||event.type?.includes("reservation")){loadAgents().catch(()=>{});loadHistory().catch(()=>{});} }

function renderTransferTargets() {
  const query=$("transferSearch").value.trim().toLowerCase(); const list=$("transferList"); list.replaceChildren();
  const matches=state.agents.filter((agent)=>`${agent.display_name} ${agent.extension}`.toLowerCase().includes(query));
  if(!matches.length)return list.append(empty("No matching agents"));
  matches.sort((a,b)=>(b.selectable-a.selectable)||a.display_name.localeCompare(b.display_name)).forEach((agent)=>{const button=document.createElement("button");button.className="transfer-target";button.disabled=!agent.selectable;const row=rowShell(agent.display_name,`Extension ${agent.extension} · ${agent.status.replaceAll("_"," ")}`,initials(agent.display_name));button.append(row);button.addEventListener("click",()=>transferCall(agent,button));list.append(button);});
}
async function openTransfer() { if(!state.call||state.call.state!==SessionState.Established)return; try{await loadAgents();renderTransferTargets();$("transferDialog").showModal();}catch(error){toast(safeError(error),true);} }
async function transferCall(agent,button) {
  button.disabled=true; const operationId=crypto.randomUUID(); let reservation=null;
  try {
    reservation=await pbx("/v1/mobile/transfers/prepare",{method:"POST",body:JSON.stringify({target_agent_id:agent.id,operation_id:operationId})});
    const target=UserAgent.makeURI(`sip:${agent.extension}@${state.pbxSession.sip.server}`); if(!target)throw new Error("Transfer target is invalid");
    await markCallExit("transfer");
    await state.call.refer(target); await pbx(`/v1/mobile/transfers/${reservation.id}/submit`,{method:"POST",body:JSON.stringify({operation_id:operationId})});
    $("transferDialog").close(); toast(`Transferring to ${agent.display_name}`);
  } catch(error){await markCallExit("clear").catch(()=>{});if(reservation)await pbx(`/v1/mobile/transfers/${reservation.id}/cancel`,{method:"POST",body:JSON.stringify({operation_id:operationId})}).catch(()=>{});toast(safeError(error),true);button.disabled=false;}
}

function buildDialpads() { [$("dialpad"),$("inCallDialpad")].forEach((pad)=>{pad.replaceChildren();"123456789*0#".split("").forEach((digit)=>{const button=document.createElement("button");button.type="button";button.className="dial-key";button.textContent=digit;button.addEventListener("click",()=>{if(pad.id==="dialpad")$("dialNumber").value+=digit;else sendDtmf(digit);});pad.append(button);});}); }
function switchPane(name){document.querySelectorAll(".nav-button").forEach((button)=>button.classList.toggle("active",button.dataset.view===name));document.querySelectorAll(".view-pane").forEach((pane)=>pane.classList.toggle("active",pane.id===`${name}View`));if(name==="history")loadHistory().catch((error)=>toast(safeError(error),true));if(name==="callbacks")loadCallbacks().catch((error)=>toast(safeError(error),true));if(name==="team")Promise.allSettled([loadQueues(),loadAgents()]);}
function clearIntervals(){state.timers.forEach(clearInterval);state.timers=[];}
async function logout(){clearIntervals();await stopPhone();await jsonRequest("/web/v1/auth/logout",{method:"POST"}).catch(()=>{});Object.assign(state,{accessToken:null,account:null,workspaces:[],membershipId:null,bootstrap:null,pbxSession:null});show("authView");}

function bind() {
  document.querySelectorAll("[data-auth-tab]").forEach((button)=>button.addEventListener("click",()=>{document.querySelectorAll("[data-auth-tab]").forEach((item)=>item.classList.toggle("active",item===button));$("joinForm").classList.toggle("hidden",button.dataset.authTab!=="join");$("loginForm").classList.toggle("hidden",button.dataset.authTab!=="login");}));
  $("joinForm").join_code.addEventListener("input",(event)=>{const value=cleanCode(event.target.value);event.target.value=value.length>4?`${value.slice(0,4)} ${value.slice(4)}`:value;});
  $("joinForm").addEventListener("submit",async(event)=>{event.preventDefault();const button=event.submitter;setBusy(button,true);const data=Object.fromEntries(new FormData(event.currentTarget));try{const result=await jsonRequest("/web/v1/enrollment/redeem",{method:"POST",body:JSON.stringify({...data,device:device()})});acceptCentralSession(result);state.enrollmentId=result.enrollment.id;state.membershipId=result.enrollment.membership_id;if(result.enrollment.status==="active")await openWorkspace(state.membershipId);else showPending({tenant_name:result.enrollment.workspace});}catch(error){toast(safeError(error),true);}finally{setBusy(button,false);}});
  $("loginForm").addEventListener("submit",async(event)=>{event.preventDefault();const button=event.submitter;setBusy(button,true);const data=Object.fromEntries(new FormData(event.currentTarget));try{const result=await jsonRequest("/web/v1/auth/login",{method:"POST",body:JSON.stringify({...data,device:device()})});acceptCentralSession(result);const active=state.workspaces.find((item)=>item.status==="active");if(active)await openWorkspace(active.id);else showPending(state.workspaces[0]||{});}catch(error){toast(safeError(error),true);}finally{setBusy(button,false);}});
  $("checkApproval").addEventListener("click",checkPending); $("logoutButton").addEventListener("click",logout); $("presenceButton").addEventListener("click",togglePresence);
  document.querySelectorAll(".nav-button").forEach((button)=>button.addEventListener("click",()=>switchPane(button.dataset.view)));
  $("workspaceSelect").addEventListener("change",(event)=>openWorkspace(event.target.value).catch((error)=>toast(safeError(error),true)));
  $("notificationButton").addEventListener("click", togglePushNotifications);
  $("callButton").addEventListener("click",placeCall); $("dialNumber").addEventListener("keydown",(event)=>{if(event.key==="Enter")placeCall();}); $("backspaceButton").addEventListener("click",()=>{$("dialNumber").value=$("dialNumber").value.slice(0,-1);});
  $("answerButton").addEventListener("click",answerCall); $("declineButton").addEventListener("click",endCall); $("hangupButton").addEventListener("click",endCall); $("muteButton").addEventListener("click",toggleMute); $("holdButton").addEventListener("click",toggleHold); $("keypadButton").addEventListener("click",()=>$("inCallDialpad").classList.toggle("hidden")); $("transferButton").addEventListener("click",openTransfer);
  $("closeTransfer").addEventListener("click",()=>$("transferDialog").close()); $("transferSearch").addEventListener("input",renderTransferTargets); $("refreshTeam").addEventListener("click",()=>Promise.allSettled([loadQueues(),loadAgents()])); $("refreshHistory").addEventListener("click",loadHistory);
  $("refreshCallbacks").addEventListener("click", loadCallbacks); document.querySelectorAll("[data-callback-filter]").forEach((button)=>button.addEventListener("click",()=>{state.callbackFilter=button.dataset.callbackFilter;document.querySelectorAll("[data-callback-filter]").forEach((item)=>item.classList.toggle("active",item===button));loadCallbacks().catch((error)=>toast(safeError(error),true));}));
  window.addEventListener("beforeinstallprompt",(event)=>{event.preventDefault();state.installPrompt=event;$("installButton").classList.remove("hidden");}); $("installButton").addEventListener("click",async()=>{await state.installPrompt?.prompt();state.installPrompt=null;$("installButton").classList.add("hidden");});
  window.addEventListener("online",()=>{if(state.membershipId)ensurePhoneHealthy().catch(()=>{});}); window.addEventListener("offline",()=>registrationStatus("offline","No connection"));
  window.addEventListener("pageshow",()=>{if(state.membershipId&&!state.call)ensurePhoneHealthy().catch(()=>{});});
  document.addEventListener("visibilitychange",()=>{if(document.visibilityState==="visible"&&state.membershipId&&!state.call)ensurePhoneHealthy().catch(()=>{});});
  navigator.serviceWorker?.addEventListener("message",(event)=>{if(event.data?.type==="call.push_open")handlePushOpen(event.data).catch((error)=>toast(safeError(error),true));});
}

async function init(){refreshIcons();buildDialpads();bind();setInterval(updateCallTimer,1000);if("serviceWorker" in navigator)state.serviceWorker=await navigator.serviceWorker.register("/sw.js").catch(()=>null);await restore();}
init();
